import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _class, _descriptor, _descriptor2;
import { action, computed, observable } from 'mobx';
import { timestampMs } from '@bufbuild/protobuf/wkt';
import { Code, ConnectError } from '@connectrpc/connect';
import { Model } from 'core/model/Model';
import { ALARM_SUPPRESSED_FILTER, POLICY_APPLICATIONS, SEVERITY_MAP } from '@kentik/ui-shared/alerting/constants';
import $auth from 'app/stores/$auth';
import $alerting from 'app/stores/alerting/$alerting';
import AlertCollection from 'app/stores/alerting/AlertCollection';
import { CorrelationAckState, CorrelationClosedReason, CorrelationConfidenceLevel, CorrelationStatus } from '@kentik/schema/gen/ts/kentik/alerting/correlation/v202609/correlation_pb';
import { Severity } from '@kentik/schema/gen/ts/kentik/alerting/types/v202303/types_pb';
import { CorrelationCloseReason } from '@kentik/schema/gen/ts/kentik/alerting/context/v202602/input_pb';
import { ackCorrelation, getCorrelation, removeAlertsFromCorrelation, unAckCorrelation, updateCorrelation } from './correlationsApi';
import { CONFIDENCE_BAND_BY_LEVEL } from './constants';

/** `alerts` is replaced by the slimmer `members`; the alerts themselves load into `alerts`. */

/** A `Get` response carries the correlation's lifecycle events alongside it. */
let CorrelationModel = (_dec = observable.ref, _class = class CorrelationModel extends Model {
  constructor(...args) {
    super(...args);
    this.memberAlerts = void 0;
    /**
     * The loaded member alerts, kept apart from `alerts` so filtering its table doesn't recompute
     * what's derived from them.
     */
    _initializerDefineProperty(this, "alertModels", _descriptor, this);
    /** When the correlation last loaded, unset until it first does. */
    _initializerDefineProperty(this, "fetchedAt", _descriptor2, this);
    /** Cleared here but not yet reported closed: the server can lag behind a close, as the list allows for too. */
    this.closePending = false;
    /** Actions taken here that the server's activity log doesn't have yet, with how many of their kind it had then. */
    this.pendingActivity = [];
  }
  get defaults() {
    return {
      title: '',
      summary: '',
      severity: Severity.UNSPECIFIED,
      status: CorrelationStatus.UNSPECIFIED,
      ackState: CorrelationAckState.UNSPECIFIED,
      ackedByUserId: BigInt(0),
      updatedByUserId: BigInt(0),
      closedByUserId: BigInt(0),
      confidenceLevel: CorrelationConfidenceLevel.UNSPECIFIED,
      closedReason: CorrelationClosedReason.UNSPECIFIED,
      mergedIntoId: '',
      activeAlertCount: 0,
      totalAlertCount: 0,
      members: [],
      activity: []
    };
  }

  /** Full alert details for the members, loaded separately via `fetchAlerts`. */
  get alerts() {
    if (!this.memberAlerts) {
      this.memberAlerts = new AlertCollection();
      // Synthetics and archived-policy alarms have no listed policy; the whole set loads, so sorting stays local.
      this.memberAlerts.requirePolicy = false;
      this.memberAlerts.serverSortableFields = [];
      // Not shown on the correlation pages.
      this.memberAlerts.fetchMitigations = false;
    }
    return this.memberAlerts;
  }
  /**
   * Member alerts still firing, from the loaded alerts once available: the server reports every
   * member of a closed correlation as inactive, whatever the alarm's own state.
   */
  get firingAlertCount() {
    const alerts = this.alertModels;
    return alerts.length > 0 ? alerts.filter(alert => !alert.isCleared).length : this.get('activeAlertCount');
  }
  get isOpen() {
    return this.get('status') !== CorrelationStatus.CLOSED;
  }
  get isAcked() {
    return this.get('ackState') === CorrelationAckState.ACKED;
  }
  get isMerged() {
    return this.get('closedReason') === CorrelationClosedReason.MERGED;
  }
  get ackUserId() {
    const ackedByUserId = this.get('ackedByUserId');
    return ackedByUserId ? String(ackedByUserId) : undefined;
  }
  get ackTimestamp() {
    const ackedAt = this.get('ackedAt');
    return ackedAt && timestampMs(ackedAt);
  }
  get statusKey() {
    return this.isOpen ? 'open' : 'cleared';
  }
  get ackKey() {
    return this.isAcked ? 'acked' : 'unacked';
  }

  /** Alert-manager severity key, e.g. `SEVERITY_MAJOR`. */
  get severityKey() {
    return SEVERITY_MAP[Severity[this.get('severity')]]?.ALERT_MANAGER;
  }
  get severityLabel() {
    return $alerting.getSeverityLabel(this.severityKey);
  }
  get confidenceBand() {
    return CONFIDENCE_BAND_BY_LEVEL[this.get('confidenceLevel')];
  }
  get createdTime() {
    const createdAt = this.get('createdAt');
    return createdAt && timestampMs(createdAt);
  }
  get updatedTime() {
    const updatedAt = this.get('updatedAt');
    return updatedAt && timestampMs(updatedAt);
  }
  get closedTime() {
    const closedAt = this.get('closedAt');
    return closedAt && timestampMs(closedAt);
  }

  /** When the correlation started, i.e. when the engine created it. */
  get startedAt() {
    return this.createdTime ?? this.firstAlertAt ?? 0;
  }

  /** When the first member alert fired, usually before the correlation started. Only `Get` carries the members. */
  get firstAlertAt() {
    const starts = this.get('members').map(member => member.startTime).filter(start => start !== undefined);
    return starts.length > 0 ? Math.min(...starts) : undefined;
  }
  endsAt(now = Date.now()) {
    return this.isOpen ? now : this.closedTime ?? now;
  }
  durationMs(now = Date.now()) {
    return Math.max(0, this.endsAt(now) - this.startedAt);
  }

  /** Groups member alerts by the rule that raised them. */
  policyKey(alert) {
    return String(alert.ruleId || alert.policyName || '');
  }

  /** One row per distinct policy across the member alerts. */
  get policies() {
    const byRule = new Map();
    this.alertModels.forEach(alert => {
      const key = this.policyKey(alert);
      const existing = byRule.get(key);
      if (existing) {
        existing.alertCount += 1;
      } else {
        byRule.set(key, {
          key,
          name: alert.policyName || 'Unnamed policy',
          policyId: alert.policyId ? String(alert.policyId) : undefined,
          application: alert.applicationLabel,
          alertCount: 1
        });
      }
    });
    return [...byRule.values()];
  }

  /** Distinct entities named by the member alerts, keyed on their displayed name. */
  get entities() {
    const byDisplay = new Map();
    this.alertModels.forEach(alert => {
      alert.typedDimensions.forEach(({
        key,
        kind,
        label,
        value,
        display
      }) => {
        const entityKey = `${kind}::${label}::${display}`;
        const existing = byDisplay.get(entityKey);
        if (existing) {
          existing.alarmIds.push(alert.id);
        } else {
          byDisplay.set(entityKey, {
            kind,
            label: label ?? key,
            value: String(value),
            display,
            alarmIds: [alert.id]
          });
        }
      });
    });
    return [...byDisplay.values()];
  }
  get entityCountsByKind() {
    const counts = {
      device: 0,
      interface: 0,
      agent: 0,
      site: 0,
      other: 0
    };
    this.entities.forEach(entity => {
      counts[entity.kind] += 1;
    });
    return counts;
  }

  /** The member alert furthest from its baseline, as reported by `$alerting.getBaseline`. */
  get peakBaseline() {
    return this.alertModels.reduce((peak, alert) => {
      const baseline = $alerting.getBaseline(alert);
      if (baseline?.percentage == null) {
        return peak;
      }
      return !peak || baseline.percentage > (peak.baseline.percentage ?? 0) ? {
        alert,
        baseline
      } : peak;
    }, null);
  }

  /** The last load failed because the correlation doesn't exist, rather than for some other reason. */
  get isNotFound() {
    return Boolean(this.error) && ConnectError.from(this.error?.body).code === Code.NotFound;
  }
  async fetch() {
    this.setRequestStatus('fetching');
    try {
      const {
        correlation,
        events
      } = await getCorrelation(this.id);
      if (!correlation) {
        throw new ConnectError(`No correlation with id ${this.id}`, Code.NotFound);
      }
      // Unset proto fields (e.g. ackedAt after an un-ack) are absent rather than cleared, so clear them explicitly.
      const next = this.deserialize({
        ...correlation,
        events
      });
      const dropped = Object.keys(this.attributes).filter(key => !(key in next));
      this.set({
        ...Object.fromEntries(dropped.map(key => [key, undefined])),
        ...next
      });
      if (this.closePending && this.isOpen) {
        this.set({
          status: CorrelationStatus.CLOSED
        });
      } else {
        this.closePending = false;
      }
      this.applyPendingActivity();
      this.markFetched();
    } catch (error) {
      this.error = {
        label: 'fetching',
        body: error
      };
      throw error;
    } finally {
      this.setRequestStatus(null);
    }
  }
  async fetchAlerts() {
    const alarmIds = this.get('members').map(member => member.alertId);
    if (alarmIds.length === 0) {
      this.alerts.set([]);
    } else {
      await this.alerts.setServerFilter({
        alarmIds: alarmIds.join(','),
        applications: Object.values(POLICY_APPLICATIONS),
        states: [],
        lookback: undefined,
        // From the first alert, which fired before the correlation started. Start-only: an active range with an
        // end makes alert-manager search alarm phase history, which is far slower.
        active: {
          start: new Date(Math.min(this.firstAlertAt ?? this.startedAt, this.startedAt)).toISOString(),
          end: null
        },
        includeArchived: true,
        // A correlation's alerts include suppressed ones; the default leaves them out.
        suppressed: ALARM_SUPPRESSED_FILTER.INCLUDED
      }, {
        force: true
      }, {
        data: {
          limit: alarmIds.length
        }
      });
    }
    this.setAlertModels(this.alerts.unfiltered);
  }
  setAlertModels(alerts) {
    this.alertModels = alerts;
  }
  markFetched() {
    this.fetchedAt = Date.now();
    this.error = null;
  }

  /** Actions report as `updating`, so the page can hold off further ones until they finish. */
  async updating(request) {
    this.setRequestStatus('updating');
    try {
      await request();
    } finally {
      this.setRequestStatus(null);
    }
    await this.fetch();
  }
  ack() {
    return this.updating(async () => {
      await ackCorrelation(this.id);
      this.recordActivity('ack', 'Acknowledged');
    });
  }
  unAck() {
    return this.updating(async () => {
      await unAckCorrelation(this.id);
      this.recordActivity('unack', 'Unacknowledged');
    });
  }
  close() {
    return this.updating(async () => {
      await updateCorrelation({
        id: this.id,
        close: true
      });
      this.closePending = true;
      this.recordActivity('resolution', 'Correlation cleared');
    });
  }

  /** Shows an action in the activity right away, until the server's log has an entry for it. */
  recordActivity(kind, details) {
    const userId = $auth.activeUser?.id;
    this.pendingActivity.push({
      entry: {
        timestamp: Date.now(),
        details,
        kind,
        userId: userId ? String(userId) : undefined
      },
      logged: this.loggedCount(kind)
    });
  }
  loggedCount(kind) {
    const pending = new Set(this.pendingActivity.map(({
      entry
    }) => entry));
    return this.get('activity').filter(entry => entry.kind === kind && !pending.has(entry)).length;
  }
  applyPendingActivity() {
    this.pendingActivity = this.pendingActivity.filter(({
      entry,
      logged
    }) => this.loggedCount(entry.kind) <= logged);
    if (this.pendingActivity.length > 0) {
      this.set({
        activity: [...this.get('activity'), ...this.pendingActivity.map(({
          entry
        }) => entry)]
      });
    }
  }
  async removeAlerts(alertIds) {
    await this.updating(() => removeAlertsFromCorrelation({
      id: this.id,
      alertIds
    }));
    await this.fetchAlerts();
  }
  deserialize({
    $typeName: _typeName,
    $unknown: _unknown,
    alerts,
    events,
    ...correlation
  }) {
    return {
      ...correlation,
      members: alerts.map(alert => ({
        alertId: alert.alertId,
        active: alert.active,
        startTime: alert.alarmStartTime && timestampMs(alert.alarmStartTime),
        addedAt: alert.addedAt && timestampMs(alert.addedAt),
        addedByUserId: alert.addedByUserId ? String(alert.addedByUserId) : undefined
      })),
      activity: (events ?? []).map(event => this.deserializeActivity(event)).filter(entry => entry !== null)
    };
  }
  deserializeActivity(event) {
    const timestamp = event.eventTime ? timestampMs(event.eventTime) : 0;
    const {
      userId,
      userName
    } = event.producer?.apiRequest ?? {};
    const user = {
      userId: userId || undefined,
      userName: userName || undefined
    };
    switch (event.input.case) {
      case 'creation':
        return {
          timestamp,
          details: event.input.value.title || 'Correlation created',
          kind: 'creation',
          // The alerts the engine grouped to open the correlation, before any joined later.
          addedAlerts: event.input.value.alertIds.length,
          alertIds: event.input.value.alertIds
        };
      case 'update':
        {
          const {
            update
          } = event.input.value;
          if (update.case === 'engineUpdate') {
            return {
              timestamp,
              details: update.value.summary || 'Correlation updated by the engine',
              kind: 'engine',
              confidence: update.value.confidence,
              addedAlerts: update.value.addedAlertIds.length,
              alertIds: update.value.addedAlertIds
            };
          }
          if (update.case === 'manualUpdate') {
            return {
              timestamp,
              details: update.value.description || 'Correlation updated manually',
              kind: 'manual',
              addedAlerts: update.value.addedAlertIds.length,
              alertIds: update.value.addedAlertIds,
              removedAlerts: update.value.removedAlertIds.length,
              ...user
            };
          }
          return {
            timestamp,
            details: 'Correlation updated'
          };
        }
      case 'resolution':
        return {
          timestamp,
          details: event.input.value.description || 'Correlation cleared',
          kind: 'resolution',
          merged: event.input.value.closeReason === CorrelationCloseReason.MERGED,
          ...user
        };
      case 'acknowledgement':
        return {
          timestamp,
          details: event.input.value.description || 'Acknowledged',
          kind: 'ack',
          ...user
        };
      case 'unacknowledgement':
        return {
          timestamp,
          details: event.input.value.description || 'Unacknowledged',
          kind: 'unack',
          ...user
        };
      default:
        return null;
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "alertModels", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "fetchedAt", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "firingAlertCount", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "firingAlertCount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isOpen", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isOpen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isAcked", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isAcked"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isMerged", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isMerged"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "firstAlertAt", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "firstAlertAt"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "policies", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "policies"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "entities", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "entities"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "entityCountsByKind", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "entityCountsByKind"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "peakBaseline", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "peakBaseline"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setAlertModels", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setAlertModels"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "markFetched", [action], Object.getOwnPropertyDescriptor(_class.prototype, "markFetched"), _class.prototype), _class);
export { CorrelationModel as default };